import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, Modal } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { auth } from '../firebaseConfig';
import {
  updateOrganization,
  removeMemberFromOrganization,
  createInvitation,
  isOrganizationOwner,
  isOrganizationAdmin,
  acceptInvitation,
  declineInvitation,
  importPersonalDataToOrganization,
  DEFAULT_PERMISSIONS,
  getMemberPermissions,
  updateMemberRole,
  updateMemberPermissions
} from '../services/organizationService';
import LandingStyleHeader from '../components/LandingStyleHeader';
import Toast from '../components/Toast';

export default function OrganizationScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const { currentOrganization, pendingInvitations, refreshOrganizations } = useOrganization();
  
  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [processingInvites, setProcessingInvites] = useState({});
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [justAcceptedOrg, setJustAcceptedOrg] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(false);

  useEffect(() => {
    document.title = 'Flomodo - Organization';
  }, []);

  useEffect(() => {
    if (currentOrganization) {
      setOrgName(currentOrganization.name);
    }
  }, [currentOrganization]);

  // Consolidate duplicate invitations to the same organization (keep most recent)
  const consolidatedInvitations = useMemo(() => {
    if (!pendingInvitations || pendingInvitations.length === 0) return [];
    
    const orgMap = new Map();
    pendingInvitations.forEach(invite => {
      const existing = orgMap.get(invite.organizationId);
      if (!existing || new Date(invite.createdAt) > new Date(existing.createdAt)) {
        orgMap.set(invite.organizationId, invite);
      }
    });
    
    return Array.from(orgMap.values());
  }, [pendingInvitations]);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
  };

  const hideToast = () => {
    setToast({ visible: false, message: '', type: 'success' });
  };

  const handleUpdateOrgName = async () => {
    if (!orgName.trim()) {
      alert('Organization name cannot be empty');
      return;
    }

    if (!isOrganizationAdmin(currentOrganization, auth.currentUser.uid)) {
      alert('Only admins can update organization details');
      return;
    }

    setLoading(true);
    try {
      await updateOrganization(currentOrganization.id, { name: orgName });
      await refreshOrganizations();
      alert('Organization name updated successfully!');
    } catch (error) {
      console.error('Error updating organization:', error);
      alert('Error updating organization: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    if (!isOrganizationAdmin(currentOrganization, auth.currentUser.uid)) {
      alert('Only admins can invite members');
      return;
    }

    setInviting(true);
    try {
      const result = await createInvitation(
        currentOrganization.id,
        currentOrganization.name,
        auth.currentUser.email,
        inviteEmail.toLowerCase().trim(),
        inviteRole
      );
      
      if (result.emailSent) {
        alert(`✅ Invitation created and email sent to ${inviteEmail}!`);
      } else {
        alert(`⚠️ Invitation created but email failed to send.\nThe user can still accept the invitation from their account.\n\nError: ${result.emailError || 'Unknown error'}`);
      }
      
      setInviteEmail('');
      setShowInviteModal(false);
      await refreshOrganizations();
    } catch (error) {
      console.error('Error inviting member:', error);
      alert('❌ Error creating invitation: ' + error.message);
    } finally {
      setInviting(false);
    }
  };

  const handleImportPersonalData = async () => {
    const confirmImport = window.confirm(
      'This will copy your personal templates and project examples into this organization.\n\n' +
      'Note: This imports from your PERSONAL collections (created when not in any organization).\n' +
      'If you created all your templates/examples while already in this organization, there may be nothing to import.\n\n' +
      'Your personal data will remain untouched. Continue?'
    );

    if (!confirmImport) return;

    setImporting(true);
    try {
      const result = await importPersonalDataToOrganization(
        auth.currentUser.uid,
        currentOrganization.id
      );
      
      if (result.personalTemplatesFound === 0 && result.personalExamplesFound === 0) {
        alert(
          '❌ No personal data found to import.\n\n' +
          `Your personal collections are empty. This likely means you created all your templates and examples while already in the "${currentOrganization.name}" organization, so they're already here!\n\n` +
          'To create personal data:\n' +
          '1. Switch to "Personal" mode (no organization)\n' +
          '2. Create templates/examples in Settings\n' +
          '3. Then import them here'
        );
      } else {
        alert(
          `Successfully imported:\n` +
          `• ${result.templatesImported} template(s) out of ${result.personalTemplatesFound} found\n` +
          `• ${result.examplesImported} example(s) out of ${result.personalExamplesFound} found`
        );
      }
    } catch (error) {
      console.error('Error importing personal data:', error);
      alert('Error importing data: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!isOrganizationAdmin(currentOrganization, auth.currentUser.uid)) {
      alert('Only admins can remove members');
      return;
    }

    if (userId === currentOrganization.ownerId) {
      alert('Cannot remove the organization owner');
      return;
    }

    if (confirm(`Are you sure you want to remove ${userName} from this organization?`)) {
      setLoading(true);
      try {
        await removeMemberFromOrganization(currentOrganization.id, userId);
        await refreshOrganizations();
        alert('Member removed successfully');
      } catch (error) {
        console.error('Error removing member:', error);
        alert('Error removing member: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAcceptInvitation = async (invite) => {
    setProcessingInvites(prev => ({ ...prev, [invite.id]: 'accepting' }));
    try {
      await acceptInvitation(invite.id, auth.currentUser.uid, auth.currentUser.email);
      setJustAcceptedOrg(invite.organizationName);
      await refreshOrganizations();
      showToast(`🎉 Welcome to ${invite.organizationName}!`, 'success');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      showToast(`Failed to accept invitation: ${error.message}`, 'error');
    } finally {
      setProcessingInvites(prev => {
        const newState = { ...prev };
        delete newState[invite.id];
        return newState;
      });
    }
  };

  const handleDeclineInvitation = async (inviteId) => {
    setProcessingInvites(prev => ({ ...prev, [inviteId]: 'declining' }));
    try {
      await declineInvitation(inviteId);
      await refreshOrganizations();
      showToast('Invitation declined', 'info');
    } catch (error) {
      console.error('Error declining invitation:', error);
      showToast(`Failed to decline invitation: ${error.message}`, 'error');
    } finally {
      setProcessingInvites(prev => {
        const newState = { ...prev };
        delete newState[inviteId];
        return newState;
      });
    }
  };

  const isAdmin = currentOrganization && isOrganizationAdmin(currentOrganization, auth.currentUser?.uid);
  const isOwner = currentOrganization && isOrganizationOwner(currentOrganization, auth.currentUser?.uid);

  // Debug logging
  console.log('Organization Screen Debug:', {
    hasOrganization: !!currentOrganization,
    isAdmin,
    isOwner,
    currentUserId: auth.currentUser?.uid,
    orgOwnerId: currentOrganization?.ownerId
  });

  return (
    <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="organization" />

      <View style={styles.contentContainer}>
          <Text style={[styles.pageTitle, { color: colors.text }]}>Organization Settings</Text>

          {/* Pending Invitations */}
          {consolidatedInvitations && consolidatedInvitations.length > 0 && (
            <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.accent, borderLeftWidth: 4 }]}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Pending Invitations</Text>
              {consolidatedInvitations.map((invite) => {
                const isProcessing = !!processingInvites[invite.id];
                const processingType = processingInvites[invite.id];
                
                return (
                  <View key={invite.id} style={[styles.inviteCard, { backgroundColor: colors.secondary, borderColor: colors.border, opacity: isProcessing ? 0.7 : 1 }]}>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.inviteOrgName, { color: colors.text }]}>{invite.organizationName}</Text>
                      <Text style={[styles.inviteFrom, { color: colors.textSecondary }]}>From: {invite.inviterEmail}</Text>
                      <Text style={[styles.inviteRole, { color: colors.accent }]}>Role: {invite.role}</Text>
                    </View>
                    <View style={styles.inviteActions}>
                      <TouchableOpacity 
                        style={[
                          styles.acceptButton, 
                          { 
                            backgroundColor: isProcessing ? colors.border : colors.accent,
                            minWidth: 90,
                            alignItems: 'center'
                          }
                        ]}
                        onPress={() => handleAcceptInvitation(invite)}
                        disabled={isProcessing}
                      >
                        {processingType === 'accepting' ? (
                          <ActivityIndicator size="small" color="#fff" />
                        ) : (
                          <Text style={[styles.buttonText, { color: '#fff' }]}>Accept</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={[
                          styles.declineButton, 
                          { 
                            backgroundColor: colors.secondary, 
                            borderColor: colors.border,
                            minWidth: 90,
                            alignItems: 'center'
                          }
                        ]}
                        onPress={() => handleDeclineInvitation(invite.id)}
                        disabled={isProcessing}
                      >
                        {processingType === 'declining' ? (
                          <ActivityIndicator size="small" color={colors.text} />
                        ) : (
                          <Text style={[styles.buttonText, { color: colors.text }]}>Decline</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          )}
          
          {/* Success Message After Accepting */}
          {justAcceptedOrg && consolidatedInvitations.length === 0 && (
            <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.accent, borderLeftWidth: 4 }]}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>✨ Welcome to {justAcceptedOrg}!</Text>
              <Text style={[styles.welcomeText, { color: colors.textSecondary, marginTop: 10 }]}>
                You're now a member of this organization. You can now:
              </Text>
              <View style={{ marginLeft: 20, marginTop: 10, gap: 8 }}>
                <Text style={[styles.welcomeItem, { color: colors.text }]}>• Access shared templates and examples</Text>
                <Text style={[styles.welcomeItem, { color: colors.text }]}>• Collaborate with team members</Text>
                <Text style={[styles.welcomeItem, { color: colors.text }]}>• Contribute to organization resources</Text>
              </View>
            </View>
          )}

          {/* Organization Details */}
          {currentOrganization && (
            <>
              <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Organization Name</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                  value={orgName}
                  onChangeText={setOrgName}
                  editable={isAdmin}
                  placeholderTextColor={colors.textTertiary}
                />
                {isAdmin && (
                  <TouchableOpacity 
                    style={[styles.saveButton, { backgroundColor: colors.accent }]}
                    onPress={handleUpdateOrgName}
                    disabled={loading || orgName === currentOrganization.name}
                  >
                    {loading ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={[styles.buttonText, { color: '#fff' }]}>Save Changes</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Import Personal Data Section */}
              <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Import Personal Data</Text>
                <Text style={[styles.sectionDescription, { color: colors.textSecondary }]}>
                  Copy templates and examples from your PERSONAL collections (created when in "Personal" mode) into this organization. Your personal data will remain intact.
                </Text>
                <View style={[styles.warningBox, { backgroundColor: colors.accent + '15', borderLeftColor: colors.accent }]}>
                  <Text style={[styles.warningText, { color: colors.textSecondary }]}>
                    💡 Note: If you created all your templates/examples while already in this organization, they're already here and there's nothing to import!
                  </Text>
                </View>
                <TouchableOpacity 
                  style={[styles.importButton, { backgroundColor: colors.accent }]}
                  onPress={handleImportPersonalData}
                  disabled={importing}
                >
                  {importing ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={[styles.buttonText, { color: '#fff' }]}>
                      📥 Import From Personal Collections
                    </Text>
                  )}
                </TouchableOpacity>
              </View>

              {/* Members Section */}
              <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.sectionHeader}>
                  <Text style={[styles.sectionTitle, { color: colors.text }]}>
                    Members ({currentOrganization.members?.length || 0})
                  </Text>
                  {isAdmin && (
                    <TouchableOpacity 
                      style={[styles.inviteButton, { backgroundColor: colors.accent }]}
                      onPress={() => setShowInviteModal(true)}
                    >
                      <Text style={[styles.buttonText, { color: '#fff' }]}>+ Invite Member</Text>
                    </TouchableOpacity>
                  )}
                </View>

                <View style={styles.membersList}>
                  {currentOrganization.members?.map((member, index) => (
                    <View key={index} style={[styles.memberCard, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.memberEmail, { color: colors.text }]}>{member.email}</Text>
                        <View style={styles.memberMeta}>
                          <Text style={[styles.memberRole, { 
                            color: member.role === 'owner' ? colors.accent : colors.textSecondary,
                            fontWeight: member.role === 'owner' ? '600' : '400'
                          }]}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </Text>
                          <Text style={[styles.memberJoined, { color: colors.textSecondary }]}>
                            Joined: {new Date(member.joinedAt).toLocaleDateString()}
                          </Text>
                        </View>
                      </View>
                      {isAdmin && member.userId !== currentOrganization.ownerId && (
                        <TouchableOpacity 
                          style={[styles.removeButton, { backgroundColor: '#ff4444' }]}
                          onPress={() => handleRemoveMember(member.userId, member.email)}
                          disabled={loading}
                        >
                          <Text style={[styles.buttonText, { color: '#fff' }]}>Remove</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                </View>
              </View>

              {/* Info Box */}
              <View style={[styles.infoBox, { backgroundColor: colors.secondary, borderLeftColor: colors.accent }]}>
                <Text style={[styles.infoTitle, { color: colors.accent }]}>About Organizations</Text>
                <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                  • All templates and examples are shared within your organization
                </Text>
                <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                  • Admins can invite members and manage organization settings
                </Text>
                <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                  • Only the owner can delete the organization
                </Text>
              </View>
            </>
          )}
        </View>

      {/* Invite Modal */}
      <Modal
        visible={showInviteModal}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowInviteModal(false)}
      >
        <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.7)' }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.primary, borderColor: colors.border }]}>
            <Text style={[styles.modalTitle, { color: colors.text }]}>Invite Member</Text>
            
            <Text style={[styles.inputLabel, { color: colors.text }]}>Email Address</Text>
            <TextInput
              style={[styles.input, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
              placeholder="member@example.com"
              placeholderTextColor={colors.textTertiary}
              value={inviteEmail}
              onChangeText={setInviteEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />

            <Text style={[styles.inputLabel, { color: colors.text }]}>Role</Text>
            <View style={styles.roleSelector}>
              <TouchableOpacity 
                style={[
                  styles.roleOption,
                  { backgroundColor: colors.secondary, borderColor: colors.border },
                  inviteRole === 'member' && { borderColor: colors.accent, borderWidth: 2 }
                ]}
                onPress={() => setInviteRole('member')}
              >
                <Text style={[styles.roleText, { color: inviteRole === 'member' ? colors.accent : colors.text }]}>Member</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[
                  styles.roleOption,
                  { backgroundColor: colors.secondary, borderColor: colors.border },
                  inviteRole === 'admin' && { borderColor: colors.accent, borderWidth: 2 }
                ]}
                onPress={() => setInviteRole('admin')}
              >
                <Text style={[styles.roleText, { color: inviteRole === 'admin' ? colors.accent : colors.text }]}>Admin</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.modalActions}>
              <TouchableOpacity 
                style={[styles.modalButton, { backgroundColor: colors.secondary, borderColor: colors.border }]}
                onPress={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('member');
                }}
              >
                <Text style={[styles.buttonText, { color: colors.text }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.modalButton, { backgroundColor: colors.accent }]}
                onPress={handleInviteMember}
                disabled={inviting}
              >
                {inviting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={[styles.buttonText, { color: '#fff' }]}>Send Invitation</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Toast Notification */}
      <Toast 
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onHide={hideToast}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    width: '100%',
    paddingTop: 100,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 60,
    width: '100%',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 30,
  },
  section: {
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 15,
  },
  sectionDescription: {
    fontSize: 14,
    marginBottom: 15,
    lineHeight: 20,
  },
  importButton: {
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginTop: 10,
  },
  warningBox: {
    borderLeftWidth: 3,
    borderRadius: 8,
    padding: 12,
    marginTop: 10,
    marginBottom: 5,
  },
  warningText: {
    fontSize: 13,
    lineHeight: 18,
  },
  input: {
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    borderWidth: 1,
    marginBottom: 10,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 10,
  },
  saveButton: {
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginTop: 10,
  },
  inviteButton: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  membersList: {
    gap: 10,
  },
  memberCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
  },
  memberEmail: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 5,
  },
  memberMeta: {
    flexDirection: 'row',
    gap: 15,
  },
  memberRole: {
    fontSize: 13,
  },
  memberJoined: {
    fontSize: 13,
  },
  removeButton: {
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  inviteCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 10,
  },
  inviteOrgName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  inviteFrom: {
    fontSize: 13,
    marginBottom: 4,
  },
  inviteRole: {
    fontSize: 13,
    fontWeight: '500',
  },
  inviteActions: {
    flexDirection: 'row',
    gap: 8,
  },
  acceptButton: {
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  declineButton: {
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
  },
  infoBox: {
    padding: 15,
    borderRadius: 8,
    borderLeftWidth: 4,
    marginTop: 10,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  infoText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 5,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    borderWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 20,
  },
  roleSelector: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 20,
  },
  roleOption: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
  },
  roleText: {
    fontSize: 15,
    fontWeight: '500',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  modalButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
  },
  welcomeText: {
    fontSize: 14,
    lineHeight: 20,
  },
  welcomeItem: {
    fontSize: 14,
    lineHeight: 22,
  },
});

